/**
 * WebView Bridge - Utility for communicating with React Native WebView
 * 
 * This utility provides functions to send messages to a React Native WebView
 * that might be wrapping this web application.
 */

export enum WebViewEvents {
  USER_SIGNIN = 'user:logged-in',
  USER_SIGNOUT = 'user:logged-out',
  BACKGROUND_COLOR_CHANGE = 'app:background-color-change',
  USER_SELECTED_PLAN = 'user:selected-plan',
  TOTAL_EXPENSES_THIS_MONTH = 'wallets:total-expenses-this-month',
  TRANSACTION_CREATED = 'transaction:created',
}

interface WebViewMessage {
  event: string;
  eventData?: any;
}

/**
 * Sends a message to the React Native WebView if it exists
 * @param event The event name
 * @param eventData Optional data to send with the event
 */
export const postToWebView = (event: string, eventData?: any): void => {
  try {
    // @ts-ignore
    if (window.ReactNativeWebView) {
      console.debug(
        `Inside ReactNativeWebView context, emitting event ${event} to ReactNativeWebView`
      );
      const message: WebViewMessage = {
        event,
        eventData: eventData || {}
      };
      // @ts-ignore
      window.ReactNativeWebView.postMessage(JSON.stringify(message));
    }
  } catch (e: any) {
    console.error(
      `Error emitting ${event} event to ReactNativeWebView`,
      e.message
    );
    console.error(e);
  }
};

/**
 * Notifies the React Native WebView that a user has signed in
 * @param email The ID of the user who signed in
 * @param token The authentication token
 */
export const notifySignIn = (email: string, token: string): void => {
  postToWebView(WebViewEvents.USER_SIGNIN, { email, token });
};

/**
 * Notifies the React Native WebView that a user has signed out
 * @param emailSaved Optional email that was saved for future use
 */
export const notifySignOut = (emailSaved?: string | null): void => {
  postToWebView(WebViewEvents.USER_SIGNOUT, { emailSaved: emailSaved || "" });
};

/**
 * Notifies the React Native WebView about a background color change
 * @param color The new background color value (hex, rgb, etc.)
 */
export const notifyBackgroundColorChange = (color: string): void => {
  console.log('notifyBackgroundColorChange', color);
  postToWebView(WebViewEvents.BACKGROUND_COLOR_CHANGE, { backgroundColor: color });
};

export const notifyUserSelectedPlan = (plan: {
  id: string;
  price: number;
  duration: number;
  durationUnit: string;
}): void => {
  console.log('notifyUserSelectedPlan', { event: WebViewEvents.USER_SELECTED_PLAN, plan });
  postToWebView(WebViewEvents.USER_SELECTED_PLAN, { plan });
};

export const notifySpendingThisMonth = (amount: number): void => {
  console.log('notifySpendingThisMonth', { event: WebViewEvents.TOTAL_EXPENSES_THIS_MONTH, amount });
  postToWebView(WebViewEvents.TOTAL_EXPENSES_THIS_MONTH, { amount });
};

export interface TransactionCreatedPayload {
  transactionType: string;
  amount: number;
  walletName?: string;
  categoryName?: string;
  description?: string;
  transactionDate: string;
}

/**
 * Notifies the React Native WebView that a transaction has been created
 * @param payload The transaction details
 */
export const notifyTransactionCreated = (payload: TransactionCreatedPayload): void => {
  console.log('notifyTransactionCreated', { event: WebViewEvents.TRANSACTION_CREATED, payload });
  postToWebView(WebViewEvents.TRANSACTION_CREATED, payload);
};